import AchievementCarouselItem from '@/components/AchievementCarouselItem';
import CourseSection from '@/components/CourseSection';
import EmptySection from '@/components/EmptySection';
import GoBackMenu from '@/components/GoBackNavbar';
import Loader from '@/components/Loader';
import Constants from '@/Constants';
import { useContentLanguageChange } from '@/hooks/useContentLanguageChange';
import { useGet } from '@/hooks/useGet';
import { FollowersRequest } from '@/models/FollowersRequest';
import { Person, SocialPerson } from '@/models/Person';
import { PersonBadge } from '@/models/PersonBadge';
import { SlideConfig } from '@/models/SlideConfig';
import { analyticsService, FlowEvents } from '@/services/AnalyticsService';
import { getDisplayName } from '@/services/UtilService';
import { ApplicationState } from '@/store';
import {
    clearFollowers,
    clearFollowing,
    clearTotalFollowers,
    deleteFollowerRequest,
    getFollowersRequest,
    setFollowerRequest,
} from '@/store/followers/action';
import { coursesPublishedUserContentsClear } from '@/store/list/action';
import { getPublicPersonClear, getPublicPersonRequest } from '@/store/person/action';
import { Divider } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import MagicSliderDots from 'react-magic-slider-dots';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import Slider from 'react-slick';
import FollowModal from '../Profile/FollowModal';
import './style.scss';

const slideConfig: SlideConfig = {
    slidesToScroll: 3,
    slidesToShow: 3,
};

const PublicProfilePage: React.FC = () => {
    const personId = parseInt(useParams<{ id: string }>().id);

    const { myPersonId, loading, publicPerson, strings, listTotal, listLoading, followers, totalFollowers } = useSelector(
        (state: ApplicationState) => ({
            myPersonId: state.identity.data.person.id,
            loading: state.people.loading,
            publicPerson: state.people.publicPerson || ({} as Person),
            strings: state.locale.data,
            followers: state.followers.data.followers,
            totalFollowers: state.followers.data.totalFollowers,
            listTotal: state.lists.listData.listPublishedUserContentsStudentView.total,
            listLoading: state.lists.listData.listPublishedUserContentsStudentView.loading,
        }),
    );

    const [showFollowersModal, setShowFollowersModal] = useState<boolean>(false);
    const [following, setFollowing] = useState<boolean>(false);
    const [followersAmount, setFollowersAmount] = useState<number>(0);

    const dispatch = useDispatch();

    const { data } = useGet<PersonBadge[]>(`${Constants.CAST_GET_ACHIEVEMENTS_URI.replace('%p', String(personId))}?onlyGranted=true`);

    const clearAll = () => {
        dispatch(clearFollowers());
        dispatch(clearTotalFollowers());
        dispatch(clearFollowing());
        dispatch(getPublicPersonClear());
        dispatch(coursesPublishedUserContentsClear());
    };

    useEffect(() => {
        if (followers && !showFollowersModal) {
            const isFollowing = followers.filter((follower: SocialPerson) => follower.id === myPersonId);
            setFollowing(isFollowing.length > 0);
            setFollowersAmount(totalFollowers);
        }
    }, [followers]);

    useEffect(() => {
        if (personId) {
            dispatch(getPublicPersonRequest(personId));
            dispatch(getFollowersRequest({ personId, pagination: { offset: 0, limit: 1 } } as FollowersRequest));
        }

        return () => {
            clearAll();
        };
    }, [personId, strings._LANGUAGE]);

    const follow = () => {
        setFollowing(true);
        analyticsService.logEvent(FlowEvents.FOLLOW_PUB_PROFILE);
        setFollowersAmount(followersAmount + 1);
        dispatch(setFollowerRequest(personId, personId === myPersonId));
    };

    const unFollow = () => {
        setFollowing(false);
        setFollowersAmount(followersAmount - 1);
        dispatch(deleteFollowerRequest(personId, personId === myPersonId));
    };

    const openFollowModal = () => {
        setShowFollowersModal(true);
    };

    useContentLanguageChange({
        onChange: () => {
            dispatch(getPublicPersonRequest(personId));
            dispatch(getFollowersRequest({ personId, pagination: { offset: 0, limit: 1 } } as FollowersRequest));
        },
        unmountChange: () => clearAll(),
    });

    const getCarouselDots = (dots: boolean): JSX.Element => <MagicSliderDots dotWidth={30} dots={dots} numDotsToShow={3} />;

    return (
        <>
            <GoBackMenu />
            <div className="public-profile-page">
                {publicPerson.id && !loading ? (
                    <>
                        <FollowModal ownerId={publicPerson.id} setShow={setShowFollowersModal} show={showFollowersModal} />

                        <div className="profile">
                            <div className="user-image">
                                {publicPerson.thumbnail ? (
                                    <img alt={getDisplayName(publicPerson)} className="user-avatar" src={publicPerson.thumbnail} />
                                ) : (
                                    <i className="material-icons-outlined">{Constants.icons.PERSON}</i>
                                )}
                            </div>
                            <div className="user-info">
                                <p className="display-name">{getDisplayName(publicPerson)}</p>
                                <span className="position">{publicPerson.position}</span>
                                <p className="about-me">{publicPerson.aboutMe}</p>
                                {(publicPerson.email || publicPerson.departmentName) && (
                                    <div className="details">
                                        <span className="email">{publicPerson.email}</span>
                                        <span className="department-name">{publicPerson.departmentName}</span>
                                    </div>
                                )}
                                <div className="container achievements">
                                    <Slider
                                        appendDots={getCarouselDots}
                                        dots={true}
                                        infinite={false}
                                        slidesToScroll={slideConfig.slidesToScroll}
                                        slidesToShow={slideConfig.slidesToShow}
                                        speed={500}
                                    >
                                        {data?.map((personBadge) => (
                                            <AchievementCarouselItem key={personBadge.id} personBadge={personBadge} />
                                        ))}
                                    </Slider>
                                </div>
                            </div>
                            <div className="user-followers-count">
                                <button className="btn btn-followers-amount" disabled={followersAmount === 0} onClick={openFollowModal}>
                                    <span>{followersAmount}</span>
                                </button>
                                <span>{followersAmount === 1 ? strings.FOLLOWER : strings.FOLLOWERS}</span>
                                {personId !== myPersonId && (
                                    <button
                                        className={`btn btn-primary ${following ? 'btn-unfollow' : ''}`}
                                        onClick={() => (following ? unFollow() : follow())}
                                        type="button"
                                    >
                                        {following ? strings.FOLLOWING : strings.FOLLOW}
                                    </button>
                                )}
                            </div>
                        </div>

                        <Divider />

                        <div className="content">
                            <CourseSection mode={'view'} viewType={'listPublishedUserContentsStudentView'} />
                            {listTotal === 0 && !listLoading && (
                                <div className="empty-container">
                                    <EmptySection iconName={Constants.icons.SMS_FAILED} message={strings.NO_CONTENTS_YET} />
                                </div>
                            )}
                            {listLoading && <Loader />}
                        </div>
                    </>
                ) : (
                    <Loader />
                )}
            </div>
        </>
    );
};
export default PublicProfilePage;
